import { z } from 'zod';
import { experimentSchema, intakeNoteSchema, shippedChangeSchema } from './domain.js';
import { count, isoDate, ratio, severityEnum } from './primitives.js';
/*
 * GET /landing (v2) — the landing-intelligence tab contract.
 *
 * One payload per landing page (?handle=, default 'provetime'; 'alle' = the
 * aggregate across all five LPs). Four data blocks + the curated playbook:
 *
 *   funnel   — per-day Meta booking-funnel clicks → ad landings (beacon) →
 *              modal opens → heuristically matched bookings.
 *   clarity  — Microsoft Clarity behavior rows (clarity_daily), per-day +
 *              range totals + latest-day device split. available=false until
 *              the Clarity sync has produced rows.
 *   speed    — PageSpeed Insights lab snapshots (psi_daily), latest per
 *              strategy + a 14-day trend.
 *   insights — server-evaluated rule hits as CODES + numeric params; the
 *              client renders the nb/en sentences (same pattern as the ad
 *              verdict reasons).
 *
 * Honesty notes baked into the shape:
 *   - adLandings counts beacon 'visit' rows WITH an fbclid — i.e. ad-ish
 *     traffic only, NOT total traffic. Total traffic = Clarity humanSessions.
 *   - metaClicks span ALL booking LPs (Meta campaign-level), so landRatePct is
 *     null on per-LP views and only computed for 'alle'.
 *   - modalOpens are recorded for everyone (ads + organic), so
 *     modalPerAdLandingPct is directional only and can exceed 100.
 *   - matchedBookings come from the HEURISTIC CAPI pairing and OVER-COUNT —
 *     Meta Ads Manager stays the source of truth (the trustMeta caveat).
 */
const ok = z.literal(true);
// ---- handles ----------------------------------------------------------------
export const landingHandleEnum = z.enum([
    'provetime',
    'lege-anbefalt',
    'kroniske-smerter',
    'etter-operasjon',
    'trening-over-70',
]);
/** The ?handle= request value: one LP, or 'alle' for the cross-LP aggregate. */
export const landingHandleParamEnum = z.enum([
    'provetime',
    'lege-anbefalt',
    'kroniske-smerter',
    'etter-operasjon',
    'trening-over-70',
    'alle',
]);
/** Lenient request inputs (handlers tolerate junk + default), mirrors rangeQuery. */
export const landingIntelQuery = z.object({
    from: isoDate.optional().catch(undefined),
    to: isoDate.optional().catch(undefined),
    handle: landingHandleParamEnum.optional().catch(undefined),
});
// ---- funnel ------------------------------------------------------------------
export const landingFunnelDaySchema = z.object({
    date: isoDate,
    /** Meta link clicks for booking-classified campaigns — ALL booking LPs, not per-LP. */
    metaClicks: count,
    /** Beacon 'visit' rows with an fbclid for this LP — ad landings, not total traffic. */
    adLandings: count,
    /** Booking-modal opens (recorded for ALL traffic, ads + organic). */
    modalOpens: count,
    /** Heuristic CAPI 'Schedule' matches dated by match day — over-counts (trust Meta). */
    matchedBookings: count,
});
export const landingFunnelTotalsSchema = z.object({
    metaClicks: count,
    adLandings: count,
    modalOpens: count,
    matchedBookings: count,
});
export const landingFunnelRatesSchema = z.object({
    /** adLandings ÷ metaClicks (%) — ONLY on 'alle' (per-LP it's apples-to-oranges → null). */
    landRatePct: ratio,
    /** modalOpens ÷ adLandings (%) — DIRECTIONAL: opens include non-ad traffic, can exceed 100. */
    modalPerAdLandingPct: ratio,
    /** modalOpens ÷ Clarity humanSessions (%) — the honest CTA-visibility rate; null without Clarity. */
    modalPerSessionPct: ratio,
});
export const landingFunnelSchema = z.object({
    days: z.array(landingFunnelDaySchema),
    totals: landingFunnelTotalsSchema,
    rates: landingFunnelRatesSchema,
});
// ---- booking attempts (OUR beacons — per-attempt journal, no Clarity quota) ----
// Every booking-modal open and completion beacon, paired into attempts with an
// outcome. This is the daily "what actually happened at the booking step"
// feed: who opened, from which ad, on what device, and whether it completed —
// including the overlay watchdog (a completion signal with no Momence booking
// behind it = the false-success class of bug, caught automatically).
export const attemptOutcomeEnum = z.enum([
    'booked', // lp_booked beacon arrived AND a Schedule match confirmed it
    'pending_no_booking', // lp_booked arrived but NO Momence booking followed (watchdog!)
    'no_completion', // modal opened, no completion signal — looked and left
]);
export const bookingAttemptSchema = z.object({
    at: z.string(), // ISO — modal-open time (or lp_booked time when unpaired)
    date: isoDate, // Oslo day
    handle: landingHandleEnum.nullable(),
    ad: z
        .object({
        adId: z.string().nullable(),
        adName: z.string().nullable(),
        campaign: z.string().nullable(),
    })
        .nullable(),
    device: z.enum(['mobile', 'desktop', 'unknown']),
    /** FB/IG in-app webview (by user-agent) — the least-forgiving environment. */
    inApp: z.boolean(),
    outcome: attemptOutcomeEnum,
    /** Modal-open → completion beacon, in minutes (null when not completed/unpaired). */
    minutesToComplete: ratio,
    /** Deep link into Clarity's recordings list, pre-filtered to this time window. */
    clarityLink: z.string(),
});
export const landingAttemptsSchema = z.object({
    days: z.array(z.object({
        date: isoDate,
        opens: count,
        completions: count,
        /** pending_no_booking count — >0 deserves eyes on recordings. */
        watchdogFlags: count,
    })),
    /** Newest first, capped (~last 3 days, max 60 rows). */
    recent: z.array(bookingAttemptSchema),
});
// ---- clarity (Microsoft Clarity daily export) --------------------------------
export const clarityDaySchema = z.object({
    date: isoDate,
    /** Total Clarity sessions (bots included). */
    sessions: count,
    botSessions: count,
    /** sessions − botSessions — the real-traffic denominator. */
    humanSessions: count,
    distinctUsers: count,
    /** Session-weighted average; null when Clarity didn't report it. */
    pagesPerSession: ratio,
    /** Session-weighted average scroll depth (%); null when unreported. */
    scrollDepthPct: ratio,
    /** TOTAL seconds across the day's sessions (divide by sessions for the average). */
    totalTimeSec: count,
    activeTimeSec: count,
    deadClicks: count,
    rageClicks: count,
    quickbacks: count,
    excessiveScrolls: count,
    scriptErrors: count,
    errorClicks: count,
});
export const clarityDeviceRowSchema = clarityDaySchema
    .omit({ date: true })
    .extend({ device: z.string() });
export const clarityTotalsSchema = clarityDaySchema.omit({ date: true }).extend({
    /** activeTimeSec ÷ humanSessions over the range; null when no human sessions. */
    avgActiveTimeSec: ratio,
    daysWithData: count,
});
export const landingClaritySchema = z.object({
    /** false until the Clarity sync has rows for this handle/range (or table missing). */
    available: z.boolean(),
    days: z.array(clarityDaySchema),
    totals: clarityTotalsSchema.nullable(),
    /** Per-device rows from the most recent day in range that has a device breakdown. */
    deviceSplit: z.object({ date: isoDate, rows: z.array(clarityDeviceRowSchema) }).nullable(),
});
// ---- speed (PageSpeed Insights daily lab runs) --------------------------------
export const psiStrategyEnum = z.enum(['mobile', 'desktop']);
export const psiSnapshotSchema = z.object({
    date: isoDate,
    strategy: psiStrategyEnum,
    /** Lighthouse performance score 0–100; null when the run failed. */
    perfScore: ratio,
    lcpMs: ratio,
    cls: ratio,
    tbtMs: ratio,
    ttfbMs: ratio,
    /** CrUX field LCP when available (real-user); null otherwise. */
    fieldLcpMs: ratio,
});
export const psiTrendDaySchema = z.object({
    date: isoDate,
    strategy: psiStrategyEnum,
    perfScore: ratio,
    lcpMs: ratio,
});
export const landingSpeedSchema = z.object({
    available: z.boolean(),
    /** Which LP the speed data describes — 'alle' falls back to 'provetime' (the main LP). */
    handle: landingHandleEnum,
    latest: z.object({
        mobile: psiSnapshotSchema.nullable(),
        desktop: psiSnapshotSchema.nullable(),
    }),
    /** Last 14 days ending at `to` (both strategies interleaved, date-ascending). */
    trendDays: z.array(psiTrendDaySchema),
});
// ---- insights (server-evaluated rules; client renders nb/en) ------------------
export const landingInsightCodeEnum = z.enum([
    'CLARITY_NOT_CONFIGURED',
    'LOW_LAND_RATE',
    'SLOW_MOBILE',
    'HIGH_FRICTION',
    'JS_ERRORS',
    'SHALLOW_SCROLL',
    'LOW_ACTIVE_TIME',
    'LOW_MODAL_OPENS',
    'QUICKBACK',
    'GOOD_SHAPE',
    // attempts-journal era (2026-06-11):
    'OVERLAY_WATCHDOG', // completion signals with no Momence booking behind them
    'CLARITY_QUOTA', // export quota burned — data resumes after the nightly reset
    'CLARITY_NO_DATA_IN_RANGE', // token fine; range has no complete ingested days
    'ATTEMPTS_NO_COMPLETIONS', // opens but zero completions in the period
]);
export const landingInsightSchema = z.object({
    code: landingInsightCodeEnum,
    severity: severityEnum, // 'info' | 'warn' | 'critical'
    /** The actual observed numbers for message interpolation (same pattern as ad-verdict reasons). */
    params: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
});
// ---- playbook passthrough (curated content from landing-playbook.ts) ----------
export const landingPlaybookSchema = z.object({
    experiments: z.array(experimentSchema),
    experimentsSummary: z.record(z.string(), z.number()),
    intake: z.object({
        updatedAt: z.string(),
        notes: z.array(intakeNoteSchema),
        personaTags: z.array(z.string()),
    }),
    shipped: z.object({
        publishedAt: z.string(),
        theme: z.string(),
        goalFromPct: z.number(),
        reviewNote: z.string(),
        changes: z.array(shippedChangeSchema),
    }),
});
// ---- the response --------------------------------------------------------------
export const landingV2Response = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    handle: landingHandleParamEnum,
    /** The five valid handles, so the SPA's LP switcher never hardcodes them. */
    handles: z.array(landingHandleEnum),
    funnel: landingFunnelSchema,
    attempts: landingAttemptsSchema,
    clarity: landingClaritySchema,
    /** Why the clarity block may be empty — drives an honest empty state. */
    clarityStatus: z.enum(['ok', 'not_configured', 'quota', 'no_data_in_range']),
    speed: landingSpeedSchema,
    insights: z.array(landingInsightSchema),
    playbook: landingPlaybookSchema,
});
//# sourceMappingURL=landing.js.map